/**
 * hr · colaboradores, contratos e pagamentos (spec 02 §10 · telas 7.1/7.2).
 *
 * A pessoa é global; contrato e pagamento pertencem à Entidade pagadora. Dinheiro
 * atravessa a fronteira somente como decimal em string e datas permanecem
 * nominais. A política documental é dado explícito do catálogo — nunca se infere
 * de nomes como "PJ" ou "Pró-labore".
 */
import { z } from 'zod'
import { codigoDeMoeda, codigoDePais, dataNominal, email, meta, uuid } from './comum.js'

const dinheiroRhPositivo = z.string().regex(
  /^(?=\d*(?:\.\d{1,2})?$)(?=.*[1-9])\d{1,16}(?:\.\d{1,2})?$/,
  'Use valor decimal positivo.',
)
const dinheiroRhNaoNegativo = z.string().regex(
  /^\d{1,16}(?:\.\d{1,2})?$/,
  'Use valor decimal não negativo.',
)

export const competenciaRh = dataNominal.refine((valor) => valor.endsWith('-01'), {
  message: 'Competência deve usar o primeiro dia do mês.',
})
export type CompetenciaRh = z.infer<typeof competenciaRh>

export const estadoDoColaborador = z.enum(['ativo', 'desligado'])
export type EstadoDoColaborador = z.infer<typeof estadoDoColaborador>

export const estadoDoPagamentoRh = z.enum([
  'provisionado',
  'aguardando_aprovacao',
  'aprovada',
  'paga',
  'cancelada',
])
export type EstadoDoPagamentoRh = z.infer<typeof estadoDoPagamentoRh>

/** Política configurada no tipo de contratação; o nome do tipo não participa da regra. */
export const politicaDeDocumentoRh = z.enum(['nf', 'rpa', 'sem_documento'])
export type PoliticaDeDocumentoRh = z.infer<typeof politicaDeDocumentoRh>

export const buscaDeColaboradores = z.object({
  q: z.string().trim().optional(),
  status: estadoDoColaborador.optional(),
  /**
   * Um OU MAIS departamentos, separados por vírgula.
   *
   * O seletor rápido da barra manda um; a gaveta manda os que a pessoa marcou
   * (spec 7.1:40-41). O formato de lista na query é o mesmo que `exportacaoDeColaboradores`
   * logo abaixo já usa — uma forma só de mandar vetor por querystring neste módulo.
   */
  departamentoIds: z.string().trim().min(1)
    .transform((valor) => valor.split(','))
    .pipe(z.array(uuid).min(1).max(50))
    .optional(),
  tipoContratacaoId: uuid.optional(),
  sort: z.enum(['nome', 'departamento', 'contratos', 'remuneracao', 'status', 'admissao'])
    .default('nome'),
  direcao: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(10),
})
export type BuscaDeColaboradores = z.infer<typeof buscaDeColaboradores>

/** Exportação em massa: somente ids explicitamente selecionados na tela. */
export const exportacaoDeColaboradores = z.object({
  ids: z.string().transform((valor) => valor.split(',')).pipe(z.array(uuid).min(1).max(200)),
  formato: z.literal('csv'),
})
export type ExportacaoDeColaboradores = z.infer<typeof exportacaoDeColaboradores>

export const resumoDeContratoRh = z.object({
  id: uuid,
  tipoContratacaoId: uuid,
  tipoContratacaoNome: z.string(),
  entityId: uuid,
  entityNome: z.string(),
  vigenciaInicio: dataNominal,
  vigenciaFim: dataNominal.nullable(),
  /** Nota curta do contrato (`contrato_rh.observacao`); a 7.1 a mostra sob a remuneração. */
  observacao: z.string().nullable(),
})
export type ResumoDeContratoRh = z.infer<typeof resumoDeContratoRh>

export const linhaDeColaborador = z.object({
  id: uuid,
  nome: z.string(),
  departamentoId: uuid,
  departamentoNome: z.string(),
  funcaoId: uuid,
  funcaoNome: z.string(),
  contratosVigentes: z.array(resumoDeContratoRh),
  /** `null` quando o nível não vê valores financeiros. */
  remuneracaoMensalBruta: z.string().nullable(),
  moeda: codigoDeMoeda.nullable(),
  status: estadoDoColaborador,
  admissao: dataNominal.nullable(),
})
export type LinhaDeColaborador = z.infer<typeof linhaDeColaborador>

/**
 * Quantas PESSOAS ativas têm contrato vigente de cada tipo (spec 7.1:38).
 *
 * Pessoa, e não contrato: quem acumula PJ e pró-labore conta uma vez em CADA tipo, e
 * não duas no total. Até 03/09 a consulta contava contrato, e o cartão "Contratos PJ /
 * Pró-labore" somava a mesma pessoa duas vezes — o próprio cabeçalho de `rh.tsx`
 * declarava a distância e dizia que fechá-la era mudar a consulta. Foi mudada.
 */
export const contagemDeContratosRhPorTipo = z.object({
  tipoContratacaoId: uuid,
  tipoContratacaoNome: z.string(),
  total: z.number().int().nonnegative(),
})
export type ContagemDeContratosRhPorTipo = z.infer<typeof contagemDeContratosRhPorTipo>

export const kpisDeColaboradores = z.object({
  ativos: z.number().int().nonnegative(),
  desligados: z.number().int().nonnegative(),
  /** Bruto contratado; `null` sem permissão financeira. */
  remuneracaoMensalBruta: z.string().nullable(),
  /** Caixa líquido e guias são leituras paralelas, nunca substituem o bruto. */
  saidaMensalEmPagamento: z.string().nullable(),
  saidaMensalEmGuias: z.string().nullable(),
  /** Única moeda dos totais; `null` quando há moedas mistas ou valores ocultos. */
  moeda: codigoDeMoeda.nullable(),
  contratosPorTipo: z.array(contagemDeContratosRhPorTipo),
  departamentos: z.number().int().nonnegative(),
  admitidosNoAno: z.number().int().nonnegative(),
  anoDeReferencia: z.string().regex(/^\d{4}$/),
})
export type KpisDeColaboradores = z.infer<typeof kpisDeColaboradores>

export const opcaoDeFiltroRh = z.object({ id: uuid, nome: z.string() })
export type OpcaoDeFiltroRh = z.infer<typeof opcaoDeFiltroRh>

/**
 * O departamento carrega o centro de custo que ele espelha.
 *
 * Não é conveniência de tela: `departamento.centro_custo_id` é NOT NULL UNIQUE
 * (app/db/migrations/0003_catalogos.sql:284), e o UNIQUE é o que faz o 1:1 da spec 02
 * §10.1 ser fato. Mandar o id junto é o que permite ao cadastro SUGERIR o centro certo
 * em vez de casar nome com nome — que é adivinhação com cara de regra.
 */
export const opcaoDeDepartamentoRh = opcaoDeFiltroRh.extend({ centroCustoId: uuid })
export type OpcaoDeDepartamentoRh = z.infer<typeof opcaoDeDepartamentoRh>

export const filtrosDeColaboradores = z.object({
  departamentos: z.array(opcaoDeDepartamentoRh),
  tiposContratacao: z.array(opcaoDeFiltroRh),
})
export type FiltrosDeColaboradores = z.infer<typeof filtrosDeColaboradores>

export const respostaDaListaDeColaboradores = z.object({
  data: z.array(linhaDeColaborador),
  meta: meta.extend({
    kpis: kpisDeColaboradores,
    filtros: filtrosDeColaboradores,
    podeCriar: z.boolean(),
    podeEditar: z.boolean(),
    podeExportar: z.boolean(),
    veValores: z.boolean(),
  }),
})
export type RespostaDaListaDeColaboradores = z.infer<typeof respostaDaListaDeColaboradores>

export const dadosBancariosDoColaborador = z.object({
  banco: z.string().trim().min(1).nullable(),
  agencia: z.string().trim().min(1).nullable(),
  conta: z.string().trim().min(1).nullable(),
  chavePix: z.string().trim().min(1).nullable(),
})
export type DadosBancariosDoColaborador = z.infer<typeof dadosBancariosDoColaborador>

/** Ficha cadastral global: não é recortada pela Entidade pagadora. */
export const fichaGlobalDoColaborador = z.object({
  id: uuid,
  nome: z.string(),
  documento: z.string().nullable(),
  /** País de EMISSÃO do documento (PÓS-V1-018), para a tela saber como rotulá-lo. */
  pais: codigoDePais.default('BR'),
  email: z.string().nullable(),
  telefone: z.string().nullable(),
  departamentoId: uuid,
  departamentoNome: z.string(),
  funcaoId: uuid,
  funcaoNome: z.string(),
  admissao: dataNominal.nullable(),
  gestorColaboradorId: uuid.nullable(),
  gestorNome: z.string().nullable(),
  status: estadoDoColaborador,
  desligadoEm: dataNominal.nullable(),
  dadosBancarios: dadosBancariosDoColaborador.nullable(),
})
export type FichaGlobalDoColaborador = z.infer<typeof fichaGlobalDoColaborador>

export const respostaDaFichaDoColaborador = z.object({
  data: fichaGlobalDoColaborador,
  meta: z.object({
    podeEditar: z.boolean(),
    podeCriarContrato: z.boolean(),
    veValores: z.boolean(),
  }),
})
export type RespostaDaFichaDoColaborador = z.infer<typeof respostaDaFichaDoColaborador>

export const contratoRh = z.object({
  id: uuid,
  colaboradorId: uuid,
  tipoContratacaoId: uuid,
  tipoContratacaoNome: z.string(),
  politicaDocumento: politicaDeDocumentoRh,
  /**
   * Pró-labore (D-154): a competência é aprovada pelo contrato no dia do vencimento, pelo job
   * das 00:15, e nunca entra na fila 6.5. Retrato da política do tipo de contratação.
   */
  aprovadoPeloContrato: z.boolean(),
  entityId: uuid,
  entityNome: z.string(),
  /** Sempre bruto; `null` sem permissão financeira. */
  remuneracao: z.string().nullable(),
  moeda: codigoDeMoeda,
  diaPagamento: z.number().int().min(1).max(31),
  mesesAposCompetencia: z.union([z.literal(0), z.literal(1)]).default(0),
  centroCustoId: uuid,
  centroCustoNome: z.string(),
  contaContabilId: uuid,
  contaContabilNome: z.string(),
  vigenciaInicio: dataNominal,
  vigenciaFim: dataNominal.nullable(),
  vigente: z.boolean(),
  observacao: z.string().nullable(),
  /**
   * A PJ contratada — PÓS-V1-020.
   *
   * CNPJ e razão social são LIDOS da empresa apontada, nunca copiados para o contrato:
   * copiar criaria uma segunda fonte que diverge no dia em que só uma for corrigida.
   * `null` em contrato antigo, e isso NÃO o torna inválido.
   */
  empresaContratadaId: uuid.nullable(),
  empresaContratadaNome: z.string().nullable(),
  empresaContratadaDocumento: z.string().nullable(),
  /**
   * A modalidade ATUAL pede PJ (`tipo_contratacao.exige_pessoa_juridica`) — PK-03.
   *
   * Lida VIVA do catálogo, nunca copiada para o contrato (o mesmo princípio da 0129): é o
   * que deixa a ficha dizer "Pessoa jurídica contratada · não vinculada" num contrato antigo
   * sem inventar exigência retroativa (DS-211: o bloco sem dado aparece dizendo o que falta).
   */
  exigePessoaJuridica: z.boolean(),
  /**
   * A QUEM a DV paga este contrato — PK-03 (DS-516).
   *
   * Com PJ vinculada, a empresa e o perfil de pagamento PRIMÁRIO dela; sem PJ, a pessoa e os dados
   * bancários dela. Sem fallback silencioso: PJ sem perfil primário, ou modalidade PJ sem empresa,
   * vêm com `pendencia` e SEM os dados da pessoa. `null` sem permissão financeira — dado bancário
   * só a quem vê valores, e a tela o mostra só na ação de pagar.
   */
  favorecido: z.object({
    tipo: z.enum(['empresa', 'pessoa']),
    nome: z.string().nullable(),
    documento: z.string().nullable(),
    meio: z.object({
      metodo: z.string().nullable(),
      banco: z.string().nullable(),
      agencia: z.string().nullable(),
      conta: z.string().nullable(),
      pixTipo: z.string().nullable(),
      pixChave: z.string().nullable(),
      titularNome: z.string().nullable(),
      titularDocumento: z.string().nullable(),
    }).nullable(),
    pendencia: z.enum(['empresa_nao_vinculada', 'empresa_sem_perfil_de_pagamento']).nullable(),
  }).nullable().default(null),
})
export type ContratoRh = z.infer<typeof contratoRh>

export const grupoDeContratosRhPorEntidade = z.object({
  entityId: uuid,
  entityNome: z.string(),
  contratos: z.array(contratoRh),
  remuneracaoMensalBruta: z.string().nullable(),
  moeda: codigoDeMoeda.nullable(),
})
export type GrupoDeContratosRhPorEntidade = z.infer<typeof grupoDeContratosRhPorEntidade>

export const respostaDosContratosRh = z.object({
  data: z.array(grupoDeContratosRhPorEntidade),
  meta: z.object({ veValores: z.boolean(), podeEditar: z.boolean() }),
})
export type RespostaDosContratosRh = z.infer<typeof respostaDosContratosRh>

export const liquidacaoRh = z.object({
  id: uuid,
  pagamentoRhId: uuid,
  data: dataNominal,
  /** `null` quando a pessoa não pode ver valores financeiros de RH. */
  valor: z.string().nullable(),
  moeda: codigoDeMoeda,
  contaBancariaId: uuid,
  contaBancariaNome: z.string(),
  forma: z.string().nullable(),
  comprovanteAnexoId: uuid.nullable(),
  movimentoBancarioId: uuid.nullable(),
})
export type LiquidacaoRh = z.infer<typeof liquidacaoRh>

export const documentoDoPagamentoRh = z.object({
  politica: politicaDeDocumentoRh,
  anexoId: uuid,
  numero: z.string(),
  emissao: dataNominal,
})
export type DocumentoDoPagamentoRh = z.infer<typeof documentoDoPagamentoRh>

export const pagamentoRh = z.object({
  id: uuid,
  contratoRhId: uuid,
  entityId: uuid,
  entityNome: z.string(),
  competencia: competenciaRh,
  vencimento: dataNominal,
  base: z.string().nullable(),
  bonus: z.string().nullable(),
  deducao: z.string().nullable(),
  liquido: z.string().nullable(),
  moeda: codigoDeMoeda,
  status: estadoDoPagamentoRh,
  ajustadaManualmente: z.boolean(),
  ajusteMotivo: z.string().nullable(),
  documento: documentoDoPagamentoRh.nullable(),
  liquidacoes: z.array(liquidacaoRh),
})
export type PagamentoRh = z.infer<typeof pagamentoRh>

export const quadroDePagamentosProvisionadosRh = z.object({
  contrato: contratoRh,
  provisionadoAte: dataNominal,
  total: z.string().nullable(),
  pagamentos: z.array(pagamentoRh),
})
export type QuadroDePagamentosProvisionadosRh = z.infer<typeof quadroDePagamentosProvisionadosRh>

export const respostaDosPagamentosRh = z.object({
  data: z.array(pagamentoRh),
  provisionados: z.array(quadroDePagamentosProvisionadosRh),
  meta: z.object({
    veValores: z.boolean(),
    podeEditar: z.boolean(),
    bruto: z.string().nullable(),
    liquido: z.string().nullable(),
    moeda: codigoDeMoeda.nullable(),
  }),
})
export type RespostaDosPagamentosRh = z.infer<typeof respostaDosPagamentosRh>

/** Query de `GET /rh/lotes`; competência é data nominal no primeiro dia do mês. */
export const consultaDeLotesRh = z.object({ competencia: competenciaRh })
export type ConsultaDeLotesRh = z.infer<typeof consultaDeLotesRh>

/**
 * Uma linha por dia de pagamento. G17a é caixa líquido; G17b é custo bruto por
 * competência. A dedução vira guia separada e não entra de novo no lote líquido.
 */
export const linhaDeLoteRh = z.object({
  diaPagamento: z.number().int().min(1).max(31),
  vencimento: dataNominal,
  moeda: codigoDeMoeda,
  quantidadePagamentos: z.number().int().nonnegative(),
  brutoG17b: z.string().nullable(),
  liquidoG17a: z.string().nullable(),
  deducaoEmGuia: z.string().nullable(),
})
export type LinhaDeLoteRh = z.infer<typeof linhaDeLoteRh>

/** A CAP da guia é uma saída própria da agenda, nunca uma segunda linha de custo. */
export const guiaDeDeducaoRh = z.object({
  contaPagarId: uuid,
  codigo: z.string(),
  tributo: z.string(),
  vencimento: dataNominal,
  moeda: codigoDeMoeda,
  valor: z.string().nullable(),
  status: z.string(),
})
export type GuiaDeDeducaoRh = z.infer<typeof guiaDeDeducaoRh>

export const totaisDosLotesRh = z.object({
  moeda: codigoDeMoeda,
  brutoPorCompetenciaG17b: z.string().nullable(),
  liquidoDosLotesG17a: z.string().nullable(),
  deducaoEmGuias: z.string().nullable(),
  /** Prova nominal de G17b = G17a + guias, calculada no servidor. */
  reconciliado: z.boolean(),
})
export type TotaisDosLotesRh = z.infer<typeof totaisDosLotesRh>

export const respostaDosLotesRh = z.object({
  data: z.array(linhaDeLoteRh),
  guias: z.array(guiaDeDeducaoRh),
  meta: z.object({
    competencia: competenciaRh,
    veValores: z.boolean(),
    totais: z.array(totaisDosLotesRh),
  }),
})
export type RespostaDosLotesRh = z.infer<typeof respostaDosLotesRh>

const camposDoColaborador = z.object({
  nome: z.string().trim().min(2),
  /**
   * O documento pessoal, no formato do PAÍS — PÓS-V1-018.
   *
   * O banco nunca exigiu CPF aqui (`colaborador.documento` é `text NULL` sem CHECK): quem
   * bloqueava o estrangeiro era a tela, com a régua de 11 ou 14 dígitos. O contrato
   * continua exigindo que exista um documento; o FORMATO passa a ser assunto do país.
   */
  documento: z.string().trim().min(1),
  /*
    SEM `.default()` aqui, e a razão é a edição.

    `entradaDeEdicaoDoColaborador` é `camposDoColaborador.partial()` com um guarda de
    "informe ao menos um campo". Um `.default('BR')` sobrevive ao `.partial()`: parsear
    `{}` passaria a devolver `{ pais: 'BR' }`, um campo apareceria do nada e o guarda
    nunca mais dispararia — edição vazia viraria edição válida. O default vale só na
    criação, onde ele significa alguma coisa.
  */
  pais: codigoDePais.optional(),
  email: email.nullable(),
  telefone: z.string().trim().min(1).nullable(),
  departamentoId: uuid,
  funcaoId: uuid,
  admissao: dataNominal.nullable(),
  gestorColaboradorId: uuid.nullable(),
  dadosBancarios: dadosBancariosDoColaborador.nullable(),
})

export const entradaDeNovoColaborador = camposDoColaborador.extend({
  /* Na criação o país tem padrão; na edição, ausência significa "não mudou". */
  pais: codigoDePais.default('BR'),
})
export type EntradaDeNovoColaborador = z.infer<typeof entradaDeNovoColaborador>

export const entradaDeEdicaoDoColaborador = camposDoColaborador.partial().extend({
  status: estadoDoColaborador.optional(),
  desligadoEm: dataNominal.nullable().optional(),
}).superRefine((entrada, contexto) => {
  if (Object.keys(entrada).length === 0) {
    contexto.addIssue({ code: 'custom', message: 'Informe ao menos um campo para editar.' })
  }
  if (entrada.status === 'desligado' && entrada.desligadoEm === undefined) {
    contexto.addIssue({
      code: 'custom',
      path: ['desligadoEm'],
      message: 'Informe a data do desligamento.',
    })
  }
  if (entrada.status === 'ativo' && entrada.desligadoEm !== undefined && entrada.desligadoEm !== null) {
    contexto.addIssue({
      code: 'custom',
      path: ['desligadoEm'],
      message: 'Colaborador ativo não possui data de desligamento.',
    })
  }
})
export type EntradaDeEdicaoDoColaborador = z.infer<typeof entradaDeEdicaoDoColaborador>

const idsDeColaboradoresEmLote = z.array(uuid).min(1).max(50).refine(
  (ids) => new Set(ids).size === ids.length,
  { message: 'Não repita colaboradores no mesmo lote.' },
)
export const entradaDeDesligamentoEmLote = z.object({
  ids: idsDeColaboradoresEmLote,
  desligadoEm: dataNominal,
})
export type EntradaDeDesligamentoEmLote = z.infer<typeof entradaDeDesligamentoEmLote>

export const entradaDeReativacaoEmLote = z.object({ ids: idsDeColaboradoresEmLote })
export type EntradaDeReativacaoEmLote = z.infer<typeof entradaDeReativacaoEmLote>

export const entradaDeContratoRh = z.object({
  tipoContratacaoId: uuid,
  entityId: uuid,
  remuneracao: dinheiroRhPositivo,
  moeda: codigoDeMoeda,
  diaPagamento: z.number().int().min(1).max(31),
  /** Contratos antigos e clientes ainda não atualizados mantêm pagamento no próprio mês. */
  mesesAposCompetencia: z.union([z.literal(0), z.literal(1)]).default(0),
  centroCustoId: uuid,
  contaContabilId: uuid,
  vigenciaInicio: dataNominal,
  vigenciaFim: dataNominal.nullable(),
  /**
   * A empresa contratada, quando a modalidade exige PJ (PÓS-V1-020/021).
   *
   * Opcional no contrato: a exigência é da MODALIDADE, conferida no serviço contra
   * `tipo_contratacao.exige_pessoa_juridica`. Deixá-la obrigatória aqui inviabilizaria
   * Pró-labore, CLT, Estágio e RPA.
   */
  empresaContratadaId: uuid.nullish(),
}).refine((entrada) => entrada.vigenciaFim === null || entrada.vigenciaFim >= entrada.vigenciaInicio, {
  path: ['vigenciaFim'],
  message: 'O fim da vigência não pode vir antes do início.',
})
export type EntradaDeContratoRh = z.infer<typeof entradaDeContratoRh>

/** Corrige o calendário enquanto todas as competências ainda são previsões intactas. */
export const entradaDeCalendarioContratoRh = z.object({
  diaPagamento: z.number().int().min(1).max(31),
  mesesAposCompetencia: z.union([z.literal(0), z.literal(1)]),
})
export type EntradaDeCalendarioContratoRh = z.infer<typeof entradaDeCalendarioContratoRh>

/**
 * O primeiro contrato nasce na mesma transação da pessoa. Separar os dois comandos
 * deixa um cadastro órfão quando a segunda gravação falha e torna a repetição
 * impossível por causa do documento único.
 */
export const entradaDeNovoColaboradorComContrato = z.object({
  colaborador: entradaDeNovoColaborador,
  contrato: entradaDeContratoRh,
})
export type EntradaDeNovoColaboradorComContrato = z.infer<typeof entradaDeNovoColaboradorComContrato>

const baseDoAditivoRh = z.object({ vigenciaInicio: dataNominal })
export const entradaDeAditivoRh = z.discriminatedUnion('campo', [
  baseDoAditivoRh.extend({ campo: z.literal('remuneracao'), novoValor: dinheiroRhPositivo }),
  baseDoAditivoRh.extend({ campo: z.literal('centro_custo'), novoValor: uuid }),
  baseDoAditivoRh.extend({ campo: z.literal('fim_vigencia'), novoValor: dataNominal.nullable() }),
  /*
    A TROCA de PJ a partir de uma data (PK-03). Trocar a empresa é mudança contratual real — a
    pessoa passa a faturar por outro CNPJ —, e por isso é aditivo, com vigência. PREENCHER a
    empresa que faltava num contrato antigo não é: é a correção cadastral da rota
    `POST /contratos/:id/empresa-contratada` (DS-514).
  */
  baseDoAditivoRh.extend({ campo: z.literal('empresa_contratada'), novoValor: uuid }),
])
export type EntradaDeAditivoRh = z.infer<typeof entradaDeAditivoRh>

/**
 * A correção cadastral: preenche a PJ que faltava num contrato cuja modalidade a exige.
 *
 * Só preenche o que está vazio, não mexe em vigência, valores nem competências, e deixa
 * trilha. Trocar uma PJ já vinculada continua sendo aditivo (DS-514).
 */
export const entradaDeVinculoDeEmpresaContratada = z.object({ empresaContratadaId: uuid })
export type EntradaDeVinculoDeEmpresaContratada = z.infer<typeof entradaDeVinculoDeEmpresaContratada>

export const entradaDeAjusteManualRh = z.object({
  base: dinheiroRhNaoNegativo.optional(),
  bonus: dinheiroRhNaoNegativo.optional(),
  deducao: dinheiroRhNaoNegativo.optional(),
  motivo: z.string().trim().min(3, 'Informe o motivo do ajuste.'),
}).refine((entrada) => entrada.base !== undefined || entrada.bonus !== undefined || entrada.deducao !== undefined, {
  message: 'Informe ao menos um valor para ajustar.',
})
export type EntradaDeAjusteManualRh = z.infer<typeof entradaDeAjusteManualRh>

export const entradaDeRegistroDeDocumentoRh = z.object({
  anexoId: uuid,
  numero: z.string().trim().min(1),
  emissao: dataNominal,
})
export type EntradaDeRegistroDeDocumentoRh = z.infer<typeof entradaDeRegistroDeDocumentoRh>

/** Comando sem decisão financeira: identifica a competência já registrada. */
export const entradaDeEnvioParaAprovacaoRh = z.object({ pagamentoRhId: uuid })
export type EntradaDeEnvioParaAprovacaoRh = z.infer<typeof entradaDeEnvioParaAprovacaoRh>

export const entradaDeLiquidacaoRh = z.object({
  pagamentoRhId: uuid,
  data: dataNominal,
  contaBancariaId: uuid,
  forma: z.enum(['pix', 'ted', 'boleto', 'dinheiro', 'outro']).nullable().optional(),
  comprovanteAnexoId: uuid.nullable().optional(),
})
export type EntradaDeLiquidacaoRh = z.infer<typeof entradaDeLiquidacaoRh>

/** Fila idempotente do provisionamento por evento + recorrência mensal. */
export const FILA_PROVISIONAR_RH = 'rh.provisionar'
